// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import { useAppNavigate, useForm } from '@advisor-app/hooks';
import { usePayment } from '../hooks';
import { type FC, type FormEventHandler, useCallback } from 'react';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { useTranslation } from 'react-i18next';

export const Subscriptions: FC = () => {
  const { t } = useTranslation();
  const { handleInputChange, values } = useForm({ voucher: '' });
  const { applyVoucher, isLoading, error, message } = usePayment();
  const navigate = useAppNavigate();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();

      const formData = new FormData(e.currentTarget);

      const resp = await applyVoucher(formData.get('voucher') as string);
      if (resp) {
        navigate('/');
      }
    },
    [applyVoucher, navigate],
  );

  return (
    <div className="mt-8 flex flex-col self-center rounded-lg bg-primary/20 px-[70px] pb-[30px] pt-10">
      <form
        className="flex flex-col items-center"
        onSubmit={onSubmit}
        data-testid="subscription-form"
      >
        <fieldset
          className={classNames('flex flex-col items-center', {
            'space-y-[36px]': !error,
          })}
          disabled={isLoading}
        >
          <label
            htmlFor=""
            className="text-center text-xl font-semibold text-primary"
          >
            <Translation i18nKey="common.enter-licensing-key-extend" ns="common" />
          </label>
          {(error || message) && (
            <output
              className={classNames('my-2', {
                'text-primary-red': !!error,
                'text-primary-green': !!message,
              })}
              data-testid="subscription-output"
            >
              {message || (
                <Translation i18nKey="common.invalid-licensing-key" ns="common" />
              )}
            </output>
          )}
          <input
            type="text"
            className="mt-3 flex max-w-[252px] rounded-[20px] bg-section px-7 py-1.5 text-center text-base text-title-dark focus:outline-none focus:ring-0"
            onChange={handleInputChange}
            name="voucher"
            value={values.voucher}
            data-testid="voucher-input"
            placeholder={t('common.enter-licensing-key', { ns: 'common' })}
          />
        </fieldset>
        <Button
          color="secondary"
          type="submit"
          className="!mt-6"
          disabled={isLoading}
        >
          <Translation i18nKey="common.enter" ns="common" />
        </Button>
      </form>
    </div>
  );
};
